import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import firebase from '../utils/firebase';
import './email-sent-view.css';
import { User } from '../AuthenticatedView';
import ModalContainer, {
  ModalContainerIconType,
} from '../modal-container/ModalContainer';
import ModalContainerHeader from '../modal-container/ModalContainerHeader';

import AuthView from './AuthView';
import { ON_LOGIN_REDIRECT_QUERY_KEY } from '../utils/redirectAfterLogin';
import ModalContainerBody from '../modal-container/ModalContainerBody';
import ModalContainerButton, {
  ModalContainerButtonAccent,
  ModalContainerButtonTreatment,
} from '../modal-container/ModalContainerButton';
import { computeAppLaunchUrlForNoUser } from '../authed/LoggedInView';

interface LinkAnonymousUserViewProps {
  user: User | undefined;
  setAnonymousUserLinked: React.Dispatch<React.SetStateAction<boolean>>;
  logout: () => Promise<void>;
}

const LinkAnonymousUserView = ({
  user,
  setAnonymousUserLinked,
  logout,
}: LinkAnonymousUserViewProps) => {
  const [hasError, setHasError] = useState<Boolean>(false);
  const auth = getAuth(firebase);
  const { currentUser } = auth;
  const queryParams = new URLSearchParams(useLocation().search);
  const redirectPath = queryParams.get(ON_LOGIN_REDIRECT_QUERY_KEY);
  const { customToken } = useParams<{
    customToken: string;
  }>();
  const navigate = useNavigate();
  const query = new URLSearchParams();

  const loadingState = (
    <ModalContainer iconType={ModalContainerIconType.Logo}>
      <ModalContainerHeader>Loading...</ModalContainerHeader>
    </ModalContainer>
  );

  const errorState = (
    <ModalContainer iconType={ModalContainerIconType.Alert}>
      <ModalContainerHeader>Something went wrong.</ModalContainerHeader>
      <ModalContainerBody>
        <div>
          We are unable to sign you in at the moment. Please try signing up
          again from the Warp app.
        </div>
        <ModalContainerButton
          href={computeAppLaunchUrlForNoUser(query)}
          content={<>Take me to Warp</>}
          treatment={ModalContainerButtonTreatment.FullWidth}
          accent={ModalContainerButtonAccent.Primary}
        />
      </ModalContainerBody>
    </ModalContainer>
  );

  // Run a one-time function to potentially sign the anonymous user into Firebase.
  // Note that this is distinct from getting the user from Warp.
  useEffect(() => {
    const checkAnonymousUserSignedIn = async () => {
      if (customToken) {
        await signInWithCustomToken(auth, customToken);
      } else {
        // If the user has no custom token and is not signed into Firebase, they're stuck in a state that they can't get out of.
        // It is safe to display an error in this case.
        setHasError(true);
      }
    };

    if (!currentUser) {
      checkAnonymousUserSignedIn();
    }
  }, [auth, customToken, currentUser]);

  useEffect(() => {
    if (currentUser && !!user?.isAnonymous) {
      // Strip the custom token from the url at this point to prevent it from being leaked.
      if (redirectPath) {
        navigate(
          `/link_anonymous_user?${ON_LOGIN_REDIRECT_QUERY_KEY}=${redirectPath}`,
          {
            replace: true,
          }
        );
      } else {
        navigate('/link_anonymous_user', {
          replace: true,
        });
      }
    }
  }, [currentUser, navigate, user, redirectPath]);

  if (currentUser) {
    return (
      <AuthView
        headerContent={<>Please sign in to continue using Warp</>}
        showLoadingState={false}
        buttonTrackingObject={{
          segmentMessage:
            'Click Sign In Button on Login View to Link Anonymous User',
          googleAnalyticsMessage: 'Link User on Login View',
          googleAnalyticsCategory: 'Login',
        }}
        user={user}
        setAnonymousUserLinked={setAnonymousUserLinked}
        logout={logout}
      />
    );
  }

  if (hasError) {
    return errorState;
  }

  return loadingState;
};

export default LinkAnonymousUserView;
